import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { css } from '@linaria/core';

const scrollbarBarClass = css`
  position: absolute;
  right: 0;
  z-index: 1;
  width: 6px;
  border-radius: 4px;
  transition:
    width 0.2s ease-in-out,
    opacity 0.3s ease-in-out;
  opacity: 0;
  &.visible {
    opacity: 1;
  }
  &:hover {
    width: 8px;
  }
`;

const scrollbarThumbClass = css`
  position: relative;
  display: block;
  width: 100%;
  height: 0;
  border-radius: inherit;
  background-color: rgba(0, 0, 0, 0.5);
  transition: 0.3s background-color;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

interface CustomScrollbarProps {
  /** 内容当前的垂直滚动位置 */
  scrollTop: number;
  /** 可滚动内容的总高度 */
  scrollHeight: number;
  /** 容器的可见高度 */
  clientHeight: number;
  /** 当发生滚动时触发的回调函数。 */
  onScroll: (scrollTop: number) => void;
  /** 表头的高度，用于在垂直方向上偏移滚动条的起始位置。 */
  headerHeight: number;
  /** 是否在选择拖动中 */
  isSelectDragging?: boolean;
  /** 滚动事件 */
  onwheel: (event: React.WheelEvent<HTMLDivElement>) => void;
}

const CustomScrollbar: React.FC<CustomScrollbarProps> = ({
  scrollTop,
  scrollHeight,
  clientHeight,
  onScroll,
  headerHeight,
  isSelectDragging,
  onwheel,
}) => {
  const thumbRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef({ y: 0 });
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [visible, setVisible] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setVisible(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = window.setTimeout(() => {
      setVisible(false);
    }, 500);
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [scrollTop]);

  const { thumbHeight, thumbTop, scrollableContentDistance } = useMemo(() => {
    const MIN_THUMB_HEIGHT = 20; // 定义滑块的最小像素高度，避免太小

    // 如果内容没有超出容器，则不需要显示滚动条。
    if (scrollHeight <= clientHeight) {
      return { thumbHeight: 0, thumbTop: 0, scrollableContentDistance: 0 };
    }

    // 按照比例的计算滑块的高度
    const proportionalThumbHeight = (clientHeight / scrollHeight) * clientHeight;
    const thumbHeight = Math.max(proportionalThumbHeight, MIN_THUMB_HEIGHT);

    // 滑块在轨道内可供移动的总空间。
    const scrollTrackSpace = clientHeight - thumbHeight;
    // 内容区域可以滚动的总距离。
    const scrollableContentDistance = scrollHeight - clientHeight;

    // 根据当前的滚动比例，计算出滑块的垂直位置。
    //  当内容不可滚动时，避免除以零的错误。
    const scrollRatio = scrollableContentDistance > 0 ? scrollTop / scrollableContentDistance : 0;
    const thumbTop = scrollRatio * scrollTrackSpace;

    return { thumbHeight, thumbTop, scrollableContentDistance };
  }, [clientHeight, scrollHeight, scrollTop]);

  // 开始处理拖拽
  const clickThumbHandle = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    // 记录下鼠标点击时，其光标相对于滑块顶部的垂直偏移量
    dragState.current.y = e.clientY - e.currentTarget.getBoundingClientRect().top;
  }, []);

  // 结束拖拽
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 处理拖拽位移
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        if (!barRef.current || !thumbRef.current) {
          return;
        }

        // 1. 根据鼠标的实时位置，计算出滑块新的顶部位置。
        const trackTop = barRef.current.getBoundingClientRect().top;
        const mouseOffsetFromTrackTop = e.clientY - trackTop;
        const newThumbTopUnbound = mouseOffsetFromTrackTop - dragState.current.y;

        // 2. 将滑块的位置约束在轨道的有效范围内。
        const scrollTrackSpace = clientHeight - thumbHeight;
        const newThumbTop = Math.max(0, Math.min(newThumbTopUnbound, scrollTrackSpace));

        // 3. 将滑块的像素位置转换回内容的 scrollTop 值，并通知父组件。
        if (scrollTrackSpace > 0) {
          const newScrollTop = (newThumbTop / scrollTrackSpace) * scrollableContentDistance;
          onScroll(newScrollTop);
        }
      }
    },
    [isDragging, clientHeight, scrollableContentDistance, thumbHeight, onScroll],
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 处理在滚动条轨道上的点击事件。
  const clickTrackHandle = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!barRef.current || !thumbRef.current || e.target !== barRef.current) {
        return;
      }

      const currentThumbHeight = thumbRef.current.offsetHeight;
      const trackTop = barRef.current.getBoundingClientRect().top;
      // 计算滑块的中心应该移动到的新位置。
      const newThumbTop = e.clientY - trackTop - currentThumbHeight / 2;

      // 将滑块的新像素位置转换为 scrollTop 值。
      const scrollTrackSpace = clientHeight - currentThumbHeight;
      if (scrollTrackSpace > 0) {
        const newScrollTop = (newThumbTop / scrollTrackSpace) * scrollableContentDistance;
        // 滚动到新位置，并确保其值在有效范围内。
        onScroll(Math.max(0, Math.min(newScrollTop, scrollableContentDistance)));
      }
    },
    [clientHeight, scrollableContentDistance, onScroll],
  );

  // 如果内容无需滚动，则不渲染滚动条。
  if (scrollableContentDistance <= 0) {
    return null;
  }

  const isVisible = isDragging || isMouseOver || visible;

  return (
    <div
      ref={barRef}
      className={`${scrollbarBarClass} ${isVisible ? 'visible' : ''}`}
      onMouseDown={clickTrackHandle}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
      onWheel={onwheel}
      style={{
        top: `${headerHeight}px`,
        height: `calc(100% - ${headerHeight}px)`,
        pointerEvents: isSelectDragging ? 'none' : 'auto',
      }}
    >
      <div
        ref={thumbRef}
        className={scrollbarThumbClass}
        style={{
          height: `${thumbHeight}px`,
          transform: `translateY(${thumbTop}px)`,
        }}
        onMouseDown={clickThumbHandle}
      />
    </div>
  );
};

export default CustomScrollbar;
